import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Select from '@radix-ui/react-select';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import type { ApiError } from '@/api/client.types';
import { getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { ADD_COURSE_TARGET_ID } from '@/features/productTours/tourDefinitions';
import { useTourTarget } from '@/features/productTours/useTourTarget';
import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  Check,
  ChevronDown,
  Eye,
  Plus,
} from 'lucide-react';
import { useNavigate } from 'react-router';

import { useCourseCreationPermission } from './useCourseCreationPermission';
import {
  applyCourseListPreferences,
  type CourseListPreferences,
  useCourseListPreferences,
} from './useCourseListPreferences';
import { assignCourseBannerImages } from './courseBannerImages';
import { AddCourseDialog } from './AddCourseDialog';
import { CourseCard } from './CourseCard';
import { useCourses } from './useCourses';
import styles from './CourseListPage.module.css';

function getVisibleCourseCountLabel(
  visibleCourseCount: number,
  totalCourseCount: number,
) {
  if (visibleCourseCount === totalCourseCount) {
    return `Showing ${visibleCourseCount} course${visibleCourseCount === 1 ? '' : 's'}.`;
  }

  return `Showing ${visibleCourseCount} of ${totalCourseCount} course${totalCourseCount === 1 ? '' : 's'}.`;
}

// Never ApiError.message: it carries the backend's whole error body.
function getErrorMessage(error: ApiError) {
  switch (error.kind) {
    case 'permission':
      return 'You do not have permission to see these courses.';
    case 'network':
      return 'We could not reach the server. Check your connection and try again.';
    default:
      return 'Something went wrong on our side. Please try again.';
  }
}

const SORT_OPTIONS: Array<{
  label: string;
  value: CourseListPreferences['sortBy'];
}> = [
  { label: 'Date Created', value: 'created' },
  { label: 'Last Updated', value: 'lastUpdated' },
];

const STATUS_FILTERS = [
  { key: 'showLive', label: 'Live' },
  { key: 'showComplete', label: 'Complete' },
  { key: 'showArchived', label: 'Archived' },
] as const;

function getStatusFilterLabel(preferences: CourseListPreferences) {
  const selectedLabels = STATUS_FILTERS.filter(
    ({ key }) => preferences[key],
  ).map(({ label }) => label);

  if (selectedLabels.length === 0) {
    return 'None';
  }

  if (selectedLabels.length === STATUS_FILTERS.length) {
    return 'All';
  }

  return selectedLabels.join(', ');
}

function getSelectedStatusFilterCount(preferences: CourseListPreferences) {
  return STATUS_FILTERS.filter(({ key }) => preferences[key]).length;
}

export function CourseListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const addCourseTargetRef = useTourTarget(ADD_COURSE_TARGET_ID);
  const [isAddCourseOpen, setIsAddCourseOpen] = useState(false);
  const [joinedCourseName, setJoinedCourseName] = useState<string | null>(null);
  const coursesQuery = useCourses();
  const courses = coursesQuery.data ?? [];
  const userId = getKuduUserId(user) ?? user?.uid ?? null;
  const { preferences, setSortBy, toggleFilter, toggleSortDirection } =
    useCourseListPreferences(userId);
  const visibleCourses = useMemo(
    () => applyCourseListPreferences(courses, preferences),
    [courses, preferences],
  );
  const bannerAssignments = useMemo(
    () => assignCourseBannerImages(courses),
    [courses],
  );
  const { canCreateCourses, permissionCheckFailed, retryPermissionCheck } =
    useCourseCreationPermission();
  // While the check runs again, the notice stays, so the button that was
  // pressed keeps the focus; a query run again with no data drops its error
  // at once, which would otherwise take the button away mid-press. When the
  // check comes back, the notice goes and the focus moves to the action that
  // answer brings: New Course for a yes, Add Course for a no.
  const [isRetryingPermission, setIsRetryingPermission] = useState(false);
  const [focusAfterRetry, setFocusAfterRetry] = useState<
    'newCourse' | 'addCourse' | null
  >(null);
  const newCourseButtonRef = useRef<HTMLButtonElement>(null);
  const addCourseButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (focusAfterRetry === 'newCourse' && canCreateCourses) {
      newCourseButtonRef.current?.focus();
      setFocusAfterRetry(null);
    } else if (
      focusAfterRetry === 'addCourse' &&
      !canCreateCourses &&
      !permissionCheckFailed
    ) {
      addCourseButtonRef.current?.focus();
      setFocusAfterRetry(null);
    }
  }, [canCreateCourses, focusAfterRetry, permissionCheckFailed]);

  // After a sort or a filter, what shows is the next thing to say, not the join.
  useEffect(() => {
    setJoinedCourseName(null);
  }, [preferences]);

  function openAddCourse() {
    setJoinedCourseName(null);
    setIsAddCourseOpen(true);
  }

  async function retryCourseCreationCheck() {
    if (isRetryingPermission) {
      return;
    }
    setIsRetryingPermission(true);
    try {
      const result = await retryPermissionCheck();
      if (result?.isSuccess) {
        setFocusAfterRetry(result.data === true ? 'newCourse' : 'addCourse');
      }
    } finally {
      setIsRetryingPermission(false);
    }
  }
  const canAddCourse = !canCreateCourses;
  const hasNonStudentCourseRole = courses.some(
    (course) => course.userRole && course.userRole !== 'student',
  );
  const shouldRegisterAddCourseTourTarget =
    canAddCourse &&
    !coursesQuery.isPending &&
    !coursesQuery.isError &&
    !hasNonStudentCourseRole;
  const setAddCourseButton = useCallback(
    (element: HTMLButtonElement | null) => {
      addCourseButtonRef.current = element;
      if (shouldRegisterAddCourseTourTarget) {
        addCourseTargetRef(element);
      }
    },
    [addCourseTargetRef, shouldRegisterAddCourseTourTarget],
  );
  const DirectionIcon =
    preferences.sortDirection === 'desc'
      ? ArrowDownWideNarrow
      : ArrowUpNarrowWide;
  const sortDirectionLabel =
    preferences.sortDirection === 'desc'
      ? 'Sort descending — click to reverse'
      : 'Sort ascending — click to reverse';
  const statusFilterLabel = getStatusFilterLabel(preferences);
  const selectedStatusFilterCount =
    getSelectedStatusFilterCount(preferences);

  const hasCourses =
    !coursesQuery.isPending && !coursesQuery.isError && courses.length > 0;
  const isEmpty =
    !coursesQuery.isPending && !coursesQuery.isError && courses.length === 0;
  const joinedWords = joinedCourseName
    ? `${joinedCourseName} has been added to your courses.`
    : null;
  // The dialog does not announce a join, so it is said here whatever the list's
  // refresh brought back: the course, the old list, or a failure.
  const afterTheJoin = (message: string) =>
    joinedWords ? `${joinedWords} ${message}` : message;

  // Everything the page says about its state, once. With a title it shows; with
  // none it is for screen readers alone. An error speaks for itself, below.
  let status: { message: string; title?: string } | null;

  if (coursesQuery.isPending) {
    status = {
      message: 'Checking your enrollments and preparing your course dashboard.',
      title: 'Loading courses',
    };
  } else if (coursesQuery.isError && coursesQuery.error.kind === 'auth') {
    status = {
      message: 'Your session needs to be refreshed before courses can be shown.',
      title: 'Redirecting to sign in',
    };
  } else if (coursesQuery.isError) {
    status = joinedWords ? { message: joinedWords } : null;
  } else if (courses.length === 0) {
    status = {
      message: afterTheJoin(
        'Courses you teach or are enrolled in will appear here when they are available.',
      ),
      title: 'No courses yet',
    };
  } else if (visibleCourses.length === 0) {
    status = {
      message: afterTheJoin('No courses match the current filters.'),
      title: 'No matching courses',
    };
  } else {
    status = {
      message:
        joinedWords ??
        getVisibleCourseCountLabel(visibleCourses.length, courses.length),
    };
  }

  return (
    <section aria-label="Courses" className={styles.page}>
      <h1 className={styles.srOnly}>Courses</h1>
      <div className={styles.actions}>
        {permissionCheckFailed || isRetryingPermission ? (
          <p className={styles.permissionNotice} role="alert">
            {isRetryingPermission
              ? 'Checking again whether you can create courses…'
              : 'We could not check whether you can create courses.'}
            <Button
              aria-disabled={isRetryingPermission || undefined}
              onClick={() => {
                void retryCourseCreationCheck();
              }}
              variant="secondary"
            >
              Try again
            </Button>
          </p>
        ) : null}
        {canCreateCourses ? (
          <Button
            onClick={() => navigate('/app/create')}
            ref={newCourseButtonRef}
            variant="secondary"
          >
            <Plus aria-hidden="true" size={18} />
            <span>New Course</span>
          </Button>
        ) : null}
        {canAddCourse ? (
          <Button onClick={openAddCourse} ref={setAddCourseButton}>
            <Plus aria-hidden="true" size={18} />
            <span>Add Course</span>
          </Button>
        ) : null}
      </div>

      {hasCourses ? (
        <div
          aria-label="Course list controls"
          className={styles.toolbar}
          role="group"
        >
          <div className={styles.sortPill}>
            <button
              aria-label={sortDirectionLabel}
              className={styles.sortDirectionButton}
              onClick={toggleSortDirection}
              type="button"
            >
              <DirectionIcon aria-hidden="true" size={16} />
            </button>
            <Select.Root
              onValueChange={(value) =>
                setSortBy(value as CourseListPreferences['sortBy'])
              }
              value={preferences.sortBy}
            >
              <Select.Trigger
                aria-label="Sort courses by"
                className={styles.selectTrigger}
              >
                <Select.Value className={styles.triggerLabel} />
                <Select.Icon asChild>
                  <ChevronDown
                    aria-hidden="true"
                    className={styles.chevronIcon}
                    size={14}
                  />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  className={styles.selectContent}
                  position="popper"
                  sideOffset={8}
                >
                  <Select.Viewport className={styles.selectViewport}>
                    <Select.Group>
                      <Select.Label className={styles.menuHeader}>
                        Sort by:
                      </Select.Label>
                      <Select.Separator className={styles.menuDivider} />
                      {SORT_OPTIONS.map((option) => (
                        <Select.Item
                          className={styles.selectItem}
                          key={option.value}
                          value={option.value}
                        >
                          <Select.ItemIndicator
                            className={styles.itemIndicator}
                          >
                            <Check aria-hidden="true" size={16} />
                          </Select.ItemIndicator>
                          <Select.ItemText>{option.label}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.Group>
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>

          <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger
              aria-label={`Show course statuses: ${selectedStatusFilterCount} selected, ${statusFilterLabel}`}
              className={styles.dropdownTrigger}
            >
              <Eye aria-hidden="true" size={16} />
              <span className={styles.triggerLabel}>{statusFilterLabel}</span>
              <ChevronDown
                aria-hidden="true"
                className={styles.chevronIcon}
                size={14}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                className={styles.dropdownContent}
                sideOffset={8}
              >
                <DropdownMenu.Label className={styles.menuHeader}>
                  Show courses that are:
                </DropdownMenu.Label>
                <DropdownMenu.Separator className={styles.menuDivider} />
                {STATUS_FILTERS.map((filter) => (
                  <DropdownMenu.CheckboxItem
                    checked={preferences[filter.key]}
                    className={styles.dropdownItem}
                    key={filter.key}
                    onCheckedChange={() => toggleFilter(filter.key)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    <DropdownMenu.ItemIndicator
                      className={styles.itemIndicator}
                    >
                      <Check aria-hidden="true" size={16} />
                    </DropdownMenu.ItemIndicator>
                    <span>{filter.label}</span>
                  </DropdownMenu.CheckboxItem>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      ) : null}

      <Card
        className={styles.statusBox}
        data-visible={status?.title ? 'true' : 'false'}
      >
        {/* aria-live as well as the role: a modal hides the page from screen
            readers, but for what carries that attribute (hideOthers). */}
        <div
          aria-atomic="true"
          aria-live="polite"
          className={styles.statusWords}
          role="status"
        >
          {status?.title ? (
            <h2 className={styles.stateTitle}>{status.title}</h2>
          ) : null}
          {status ? <p className={styles.stateMessage}>{status.message}</p> : null}
        </div>
        {isEmpty && canAddCourse ? (
          <div className={styles.stateActions}>
            <Button onClick={openAddCourse}>
              <Plus aria-hidden="true" size={18} />
              <span>Add your first course</span>
            </Button>
          </div>
        ) : null}
      </Card>

      {coursesQuery.isError && coursesQuery.error.kind !== 'auth' ? (
        <Card className={styles.stateCard} role="alert">
          <h2 className={styles.stateTitle}>Unable to load your courses</h2>
          <p className={styles.stateMessage}>
            {getErrorMessage(coursesQuery.error)}
          </p>
          <Button
            onClick={() => {
              void coursesQuery.refetch();
            }}
            variant="primary"
          >
            Try again
          </Button>
        </Card>
      ) : null}

      {hasCourses && visibleCourses.length > 0 ? (
        <ul className={styles.grid}>
          {visibleCourses.map((course) => (
            <li className={styles.item} key={course.id}>
              <CourseCard
                bannerImageFilename={
                  bannerAssignments.get(course.id) ?? null
                }
                course={course}
                // The server copies a course only for its instructors
                // (issue #48): not for an assistant or a student of it.
                showCopyAction={
                  canCreateCourses && course.userRole === 'instructor'
                }
              />
            </li>
          ))}
        </ul>
      ) : null}
      {canAddCourse ? (
        <AddCourseDialog
          enrolledCourseIds={coursesQuery.data?.userCourseIds ?? []}
          fallbackFocusRef={addCourseButtonRef}
          onCourseAdded={setJoinedCourseName}
          onOpenChange={setIsAddCourseOpen}
          open={isAddCourseOpen}
        />
      ) : null}
    </section>
  );
}
